import React, { useState, useEffect, useCallback } from 'react';
import MainSidebar from 'components/ui/MainSidebar';
import Button from 'components/ui/Button';

import Select from 'components/ui/Select';
import Icon from 'components/AppIcon';
import ProductTable from './components/ProductTable';
import ProductModal from './components/ProductModal';
import SupplierPanel from './components/SupplierPanel';
import CSVImportPanel from './components/CSVImportPanel';
import { productCatalogService } from 'services/productCatalogService';
import { supabase } from 'lib/supabase';

const ProductCatalog = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [supplierProduct, setSupplierProduct] = useState(null);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [notification, setNotification] = useState(null);
  const [productTypeOptions, setProductTypeOptions] = useState([{ value: '', label: 'Todos los tipos' }]);

  const showNotif = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3500);
  };

  // Load product type filter options dynamically
  useEffect(() => {
    const loadProductTypes = async () => {
      const { data } = await supabase?.from('product_types')?.select('value, label')?.eq('active', true)?.order('label');
      if (data?.length) {
        setProductTypeOptions([
          { value: '', label: 'Todos los tipos' },
          ...data?.map(pt => ({ value: pt?.value, label: pt?.label })),
        ]);
      }
    };
    loadProductTypes();
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    const data = await productCatalogService?.getProducts({
      search,
      productType: filterType,
      category: filterCategory,
      brand: filterBrand,
      activeOnly: false,
    });
    setProducts(data);
    setLoading(false);
  }, [search, filterType, filterCategory, filterBrand]);

  useEffect(() => {
    const timer = setTimeout(loadProducts, 300);
    return () => clearTimeout(timer);
  }, [loadProducts]);

  const handleSaveProduct = async (formData) => {
    setSavingProduct(true);
    try {
      if (editingProduct?.id) {
        await productCatalogService?.updateProduct(editingProduct?.id, formData);
        showNotif('Producto actualizado correctamente');
      } else {
        await productCatalogService?.createProduct(formData);
        showNotif('Producto creado correctamente');
      }
      setShowModal(false);
      setEditingProduct(null);
      await loadProducts();
    } catch (err) {
      showNotif(err?.message || 'Error al guardar el producto', 'error');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeactivate = async (product) => {
    if (!window.confirm(`¿Desactivar el producto "${product?.name}"?`)) return;
    try {
      await productCatalogService?.deactivateProduct(product?.id);
      showNotif('Producto desactivado');
      await loadProducts();
    } catch (err) {
      showNotif(err?.message || 'Error al desactivar', 'error');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleViewSuppliers = (product) => {
    setSupplierProduct(prev => prev?.id === product?.id ? null : product);
  };

  const handleNewProduct = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const activeCount = products?.filter(p => p?.active)?.length;
  const totalCount = products?.length;

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <MainSidebar />

      <main className="flex-1 overflow-y-auto lg:ml-64">
        {/* Notification */}
        {notification && (
          <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-elevation-2 text-sm font-medium ${
            notification?.type === 'error' ? 'bg-error text-error-foreground' : 'bg-success text-success-foreground'
          }`}>
            <Icon name={notification?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} />
            {notification?.msg}
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground flex items-center gap-2">
                <Icon name="Package" size={28} className="text-primary" />
                Catálogo de Productos
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {activeCount} activos · {totalCount} total · Proveedores: tabla <code className="text-xs bg-muted px-1 rounded">providers</code>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowCSVImport(true)}>
                <Icon name="Upload" size={16} className="mr-2" />
                Importar CSV
              </Button>
              <Button onClick={handleNewProduct}>
                <Icon name="Plus" size={16} className="mr-2" />
                Nuevo Producto
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="relative">
                <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, marca, SKU..."
                  value={search}
                  onChange={e => setSearch(e?.target?.value)}
                  className="w-full pl-9 pr-3 h-10 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
              <Select
                options={productTypeOptions}
                value={filterType}
                onChange={setFilterType}
                placeholder="Tipo de producto"
              />
              <input
                type="text"
                placeholder="Filtrar por categoría"
                value={filterCategory}
                onChange={e => setFilterCategory(e?.target?.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <input
                type="text"
                placeholder="Filtrar por marca"
                value={filterBrand}
                onChange={e => setFilterBrand(e?.target?.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          {/* Products Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <ProductTable
              products={products}
              loading={loading}
              onEdit={handleEdit}
              onDeactivate={handleDeactivate}
              onViewSuppliers={handleViewSuppliers}
            />
          </div>

          {/* Supplier Panel */}
          {supplierProduct && (
            <SupplierPanel
              product={supplierProduct}
              onClose={() => setSupplierProduct(null)}
            />
          )}
        </div>
      </main>

      {/* Product Modal */}
      <ProductModal
        open={showModal}
        product={editingProduct}
        onClose={() => { setShowModal(false); setEditingProduct(null); }}
        onSave={handleSaveProduct}
        loading={savingProduct}
      />

      {/* CSV Import Panel */}
      {showCSVImport && (
        <CSVImportPanel
          onClose={() => setShowCSVImport(false)}
          onImportComplete={() => { loadProducts(); showNotif('Importación completada'); }}
        />
      )}
    </div>
  );
};

export default ProductCatalog;
